import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useCart } from '@/context/CartContext';
import { ShoppingCart, Filter } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Products = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchParams, setSearchParams] = useSearchParams();
  const category = searchParams.get('category');
  const { addToCart } = useCart();

  useEffect(() => {
    fetchProducts();
  }, [category]);

  const fetchProducts = async () => {
    try {
      setLoading(true);
      const url = category ? `${API}/products?category=${category}` : `${API}/products`;
      const response = await axios.get(url);
      setProducts(response.data);
    } catch (error) {
      console.error('Error fetching products:', error);
      toast.error('Failed to load products');
    } finally {
      setLoading(false);
    }
  };

  const handleAddToCart = (product) => {
    addToCart(product);
    toast.success(`${product.name} added to cart!`);
  };

  const filterByCategory = (cat) => {
    if (cat) {
      setSearchParams({ category: cat });
    } else {
      setSearchParams({});
    }
  };

  return (
    <div className="bg-off_white min-h-screen py-12 relative overflow-hidden" data-testid="products-page">
      {/* Decorative Stars */}
      <div className="absolute top-20 right-16 w-8 h-8 opacity-20">
        <div className="w-full h-full bg-brand_blue animate-pulse" style={{ clipPath: 'polygon(50% 0%, 61% 35%, 98% 35%, 68% 57%, 79% 91%, 50% 70%, 21% 91%, 32% 57%, 2% 35%, 39% 35%)' }} />
      </div>
      <div className="absolute bottom-32 left-12 w-6 h-6 opacity-15">
        <div className="w-full h-full bg-brand_red rotate-12" style={{ clipPath: 'polygon(50% 0%, 65% 40%, 100% 50%, 65% 60%, 50% 100%, 35% 60%, 0% 50%, 35% 40%)' }} />
      </div>
      <div className="absolute top-1/2 right-1/4 w-7 h-7 opacity-10">
        <div className="w-full h-full bg-brand_yellow animate-spin" style={{ clipPath: 'polygon(50% 0%, 61% 35%, 98% 35%, 68% 57%, 79% 91%, 50% 70%, 21% 91%, 32% 57%, 2% 35%, 39% 35%)', animationDuration: '20s' }} />
      </div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="text-center mb-12">
          <h1 className="font-chewy text-5xl sm:text-7xl mb-4">
            <span className="text-brand_blue">OUR</span>{' '}
            <span className="text-brand_red">PRODUCTS</span>
          </h1>
          <p className="font-nunito text-xl text-gray-700 font-semibold">
            Fresh, natural, and absolutely delicious!
          </p>
        </div>

        {/* Filter Buttons */}
        <div className="flex flex-wrap justify-center gap-4 mb-12">
          <button
            onClick={() => filterByCategory(null)}
            className={`font-nunito font-bold px-6 py-3 rounded-full border-2 border-black shadow-hard hover:shadow-hard-hover hover:-translate-y-0.5 transition-all ${
              !category ? 'bg-brand_blue text-white' : 'bg-white text-black'
            }`}
            data-testid="filter-all"
          >
            All Products
          </button>
          <button
            onClick={() => filterByCategory('lemonade')}
            className={`font-nunito font-bold px-6 py-3 rounded-full border-2 border-black shadow-hard hover:shadow-hard-hover hover:-translate-y-0.5 transition-all ${
              category === 'lemonade' ? 'bg-brand_blue text-white' : 'bg-white text-black'
            }`}
            data-testid="filter-lemonade"
          >
            Lemonades
          </button>
        </div>

        {/* Products Grid */}
        {loading ? (
          <div className="text-center py-20">
            <div className="inline-block w-16 h-16 border-4 border-brand_blue border-t-transparent rounded-full animate-spin" />
          </div>
        ) : products.length === 0 ? (
          <div className="text-center py-20">
            <p className="font-nunito text-xl text-gray-600 font-semibold">
              No products found. Check back soon!
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8">
            {products.map((product) => (
              <div
                key={product.id}
                className="bg-white border-4 border-black rounded-2xl overflow-hidden shadow-hard hover:shadow-hard-lg hover:-translate-y-2 transition-all"
                data-testid={`product-card-${product.id}`}
              >
                <Link to={`/products/${product.id}`}>
                  <img
                    src={product.image_url}
                    alt={product.name}
                    className="w-full h-56 object-cover"
                  />
                </Link>
                <div className="p-4">
                  <Link to={`/products/${product.id}`}>
                    <h3 className="font-chewy text-2xl text-brand_blue mb-2 hover:text-brand_red transition-colors">
                      {product.name}
                    </h3>
                  </Link>
                  {product.flavor && (
                    <p className="font-nunito text-sm text-gray-600 font-semibold mb-2">
                      {product.flavor}
                    </p>
                  )}
                  <p className="font-nunito text-gray-700 text-sm mb-4 line-clamp-2">
                    {product.description}
                  </p>
                  <div className="flex items-center justify-between">
                    <span className="font-chewy text-2xl text-brand_red">
                      ${product.price.toFixed(2)}
                    </span>
                    <button
                      onClick={() => handleAddToCart(product)}
                      className="bg-brand_blue text-white px-4 py-2 rounded-full font-nunito font-bold border-2 border-black shadow-hard hover:shadow-hard-hover hover:-translate-y-0.5 transition-all"
                      data-testid={`add-to-cart-${product.id}`}
                    >
                      <ShoppingCart className="inline-block w-5 h-5" strokeWidth={3} />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Products;